import { getPanchayatDataProvider } from "./panchayat-data";

import { normalizeKey } from "./normalize"
const normalizeName = normalizeKey

function matchByNames(entity: any, target: string): boolean {
  if (!entity) return false
  const names = entity.names || {}
  const candidates: string[] = [entity.id, entity.code, entity.name, names.en, names.hi, names.pa].filter(Boolean)
  const normalizedTarget = normalizeName(target)
  return candidates.some((n) => normalizeName(String(n)) === normalizedTarget)
}

let sarpanchFallbackCache: Map<string, string> | null = null

async function fetchSarpanchFromXlsx(district: string, block: string, village: string): Promise<string | null> {
  try {
    if (!sarpanchFallbackCache) {
      const res = await fetch("/data/sarpanch.xlsx")
      if (!res.ok) {
        return null
      }
      const buf = await res.arrayBuffer()
      const XLSX = await import("xlsx")
      const wb = XLSX.read(buf, { type: "array" })
      const sheet = wb.Sheets[wb.SheetNames[0]]
      const json: any[] = XLSX.utils.sheet_to_json(sheet, { raw: true })
      const cache = new Map<string, string>()
      for (const row of json) {
        const d = normalizeName(String(row["District"] ?? row["DISTRICT"] ?? row["district"] ?? row["Dist"] ?? row["DIST"]) || "")
        const b = normalizeName(String(row["Block"] ?? row["BLOCK"] ?? row["block"] ?? row["Blk"] ?? row["BLK"]) || "")
        const v = normalizeName(String(row["Village"] ?? row["VILLAGE"] ?? row["village"] ?? row["GP"] ?? row["Gram Panchayat"] ?? row["GRAM PANCHAYAT"]) || "")
        const s = String(row["Sarpanch"] ?? row["SARPANCH"] ?? row["Name"] ?? row["NAME"] ?? "").trim()
        if (d && b && v && s) {
          cache.set(`${d}|${b}|${v}`, s)
        }
      }
      sarpanchFallbackCache = cache
    }
    const key = `${normalizeName(district)}|${normalizeName(block)}|${normalizeName(village)}`
    return sarpanchFallbackCache!.get(key) ?? null
  } catch {
    return null
  }
}

/**
 * Finds the name of the Sarpanch for a given village.
 * This implementation fetches the panchayat dataset and searches for the sarpanch's name.
 * It returns null if the sarpanch is not found, instead of throwing an error for missing data.
 * It may still throw if the dataset fails to load or if the location hierarchy is invalid (e.g. district not found).
 */
export async function findSarpanch(district: string, block: string, village: string): Promise<string | null> {
  // console.log(`Searching for Sarpanch of ${village}, ${block}, ${district}`);

  try {
    const provider = getPanchayatDataProvider()
    const districts = await provider.getDistricts()
    const districtData = districts.find((entry: any) => matchByNames(entry, district))
    if (!districtData) {
      // Return null instead of throwing for missing location data to avoid console noise
      return null;
    }

    const blocks = await provider.getBlocks(districtData.id)
    const blockData = blocks.find((entry: any) => matchByNames(entry, block))
    if (!blockData) {
      return null;
    }

    const gps = await provider.getGramPanchayats(blockData.id, districtData.id)
    const villageData = gps.find((entry: any) => matchByNames(entry, village))
    if (!villageData) {
      return null;
    }

    const institution = await provider.getInstitution(villageData.id, districtData.id, blockData.id)
    if (institution?.sarpanch?.en) {
      return institution.sarpanch.en
    }

    // Attempt XLSX fallback (manually curated data)
    const fallback = await fetchSarpanchFromXlsx(district, block, village)
    if (fallback) {
      return fallback
    }
    return null;
  } catch (error) {
    console.warn("Warning in findSarpanch:", error);
    return null;
  }
}
